import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fullName } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, addDays,
  addWeeks, addMonths, addMinutes, format, isSameDay, isSameMonth, parseISO,
} from "date-fns";
import { it } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { EditAppointmentModal } from "@/components/crm/EditAppointmentModal";

const MODE_COLOR = {
  "Telefonico": "bg-blue-500", "Videocall": "bg-purple-500", "In presenza": "bg-emerald-500", "Altro": "bg-zinc-500",
};
const STATUS_OPACITY = { "EFFETTUATO": "opacity-60 line-through", "ANNULLATO": "opacity-40 line-through", "NO-SHOW": "ring-2 ring-amber-500" };

export default function CalendarPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [gEvents, setGEvents] = useState([]);
  const [gConnected, setGConnected] = useState(false);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [showGoogle, setShowGoogle] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editAppt, setEditAppt] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = () => setRefreshKey((k) => k + 1);
  const removeAppt = async (a) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa attività?")) return;
    await api.delete(`/appointments/${a.id}`);
    toast.success("Appuntamento eliminato");
    reload();
  };

  const range = useMemo(() => {
    if (view === "day") return { start: cursor, end: cursor };
    if (view === "week") return { start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
    return { start: startOfMonth(cursor), end: endOfMonth(cursor) };
  }, [view, cursor]);

  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale")));
    api.get("/google/status").then((r) => setGConnected(!!(r.data?.configured && r.data?.connected))).catch(() => setGConnected(false));
  }, [isAdmin]);

  useEffect(() => {
    setLoading(true);
    const rangeStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }).toISOString();
    const rangeEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }).toISOString();
    const apptParams = { start: rangeStart, end: rangeEnd };
    if (filterUser) apptParams.assigned_to = filterUser;
    api.get("/appointments", { params: apptParams }).then((r) => { setAppts(r.data); setLoading(false); });
    // Eventi Google (sola lettura): Admin => tutti gli utenti collegati o il commerciale filtrato;
    // Commerciale => solo i propri eventi (se collegato).
    const fetchGoogle = isAdmin || gConnected;
    if (fetchGoogle) {
      const gParams = { start: rangeStart, end: rangeEnd };
      if (isAdmin) gParams.user_id = filterUser || "all";
      api.get("/google/events", { params: gParams })
        .then((r) => setGEvents(Array.isArray(r.data) ? r.data : []))
        .catch(() => setGEvents([]));
    } else {
      setGEvents([]);
    }
  }, [cursor, filterUser, gConnected, isAdmin, refreshKey]);

  const move = (dir) => {
    if (view === "day") setCursor((c) => addDays(c, dir));
    else if (view === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addMonths(c, dir));
  };

  const sameDay = (iso, day) => { try { return isSameDay(parseISO(iso), day); } catch { return false; } };
  const itemsForDay = (day) => {
    const crm = appts.filter((a) => sameDay(a.start_at, day)).map((a) => ({ kind: "crm", ...a }));
    const g = showGoogle ? gEvents.filter((e) => sameDay(e.start_at, day)).map((e) => ({ kind: "google", ...e })) : [];
    return [...crm, ...g].sort((x, y) => (x.start_at || "").localeCompare(y.start_at || ""));
  };

  const googleAvailable = isAdmin || gConnected;
  const title = view === "month" ? format(cursor, "MMMM yyyy", { locale: it }) : view === "week" ? `${format(range.start, "d MMM", { locale: it })} – ${format(range.end, "d MMM yyyy", { locale: it })}` : format(cursor, "EEEE d MMMM yyyy", { locale: it });

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Calendario</h1>
          <p className="text-sm text-muted-foreground capitalize">{title}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Select value={filterUser || "__all"} onValueChange={(v) => setFilterUser(v === "__all" ? "" : v)}>
              <SelectTrigger className="h-9 w-44" data-testid="calendar-user-filter"><SelectValue placeholder="Tutto il team" /></SelectTrigger>
              <SelectContent><SelectItem value="__all">Tutto il team</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="flex rounded-md border overflow-hidden">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className={cn("px-3 py-1.5 text-sm capitalize transition-colors", view === v ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>
                {{ day: "Giorno", week: "Settimana", month: "Mese" }[v]}
              </button>
            ))}
          </div>
          {googleAvailable && (
            <Button
              variant={showGoogle ? "default" : "outline"}
              size="sm"
              onClick={() => setShowGoogle((s) => !s)}
              className="gap-1.5"
              data-testid="toggle-google-events"
              title={showGoogle ? "Nascondi eventi Google Calendar" : "Mostra eventi Google Calendar"}
            >
              {showGoogle ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="hidden sm:inline">{showGoogle ? "Eventi Google" : "Eventi Google nascosti"}</span>
            </Button>
          )}
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => move(-1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Oggi</Button>
            <Button variant="outline" size="icon" onClick={() => move(1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      {googleAvailable && showGoogle && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> Appuntamenti CRM</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-sky-400 bg-sky-50 dark:bg-sky-500/10 inline-flex items-center justify-center"><CalendarDays className="w-2 h-2 text-sky-600 dark:text-sky-300" /></span> Google Calendar</span>
          {isAdmin && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-zinc-400 inline-block" /> Il pallino colorato indica il commerciale</span>}
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
        view === "month" ? <MonthView cursor={cursor} itemsForDay={itemsForDay} navigate={navigate} isAdmin={isAdmin} /> :
        view === "week" ? <WeekView range={range} itemsForDay={itemsForDay} navigate={navigate} isAdmin={isAdmin} /> :
        <DayView day={cursor} items={itemsForDay(cursor)} navigate={navigate} isAdmin={isAdmin} onEdit={setEditAppt} onDelete={removeAppt} />
      )}

      <EditAppointmentModal appt={editAppt} open={!!editAppt} onClose={() => setEditAppt(null)} onDone={reload} />
    </div>
  );
}

function Chip({ item, navigate, isAdmin }) {
  const ownerName = item.kind === "google" ? item.owner_name : item.user?.name;
  const ownerColor = item.kind === "google" ? item.owner_color : item.user?.color;
  if (item.kind === "google") {
    let t = "";
    if (!item.all_day) { try { t = format(parseISO(item.start_at), "HH:mm") + " "; } catch { t = ""; } }
    return (
      <button data-testid={`gcal-event-${item.id}`} onClick={() => item.html_link && window.open(item.html_link, "_blank")}
        title={`Google Calendar${ownerName ? ` · ${ownerName}` : ""}: ${item.title}`}
        className="w-full text-left text-[11px] px-1.5 py-1 rounded border border-sky-400/70 bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 truncate flex items-center gap-1 transition-transform hover:scale-[1.02]">
        {isAdmin && ownerColor && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ownerColor }} />}
        <CalendarDays className="w-3 h-3 shrink-0" />
        <span className="truncate">{t}{item.title}{isAdmin && ownerName ? ` · ${ownerName}` : ""}</span>
      </button>
    );
  }
  return (
    <button data-testid={`appt-${item.id}`} onClick={() => item.lead && navigate(`/leads/${item.lead.id}`)}
      className={cn("w-full text-left text-[11px] px-1.5 py-1 rounded text-white truncate flex items-center gap-1 transition-transform hover:scale-[1.02]", MODE_COLOR[item.mode] || "bg-zinc-500", STATUS_OPACITY[item.status] || "")}>
      {isAdmin && ownerColor && <span className="w-2 h-2 rounded-full shrink-0 ring-1 ring-white/70" style={{ background: ownerColor }} />}
      <span className="truncate">{format(parseISO(item.start_at), "HH:mm")}–{format(addMinutes(parseISO(item.start_at), item.duration || 30), "HH:mm")} {item.lead ? fullName(item.lead) : ""}{isAdmin && ownerName ? ` · ${ownerName}` : ""}</span>
    </button>
  );
}

function MonthView({ cursor, itemsForDay, navigate, isAdmin }) {
  const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });
  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="grid grid-cols-7 border-b text-xs font-medium text-muted-foreground">
        {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const items = itemsForDay(day);
          return (
            <div key={day.toISOString()} className={cn("min-h-[92px] border-b border-r p-1 space-y-1", !isSameMonth(day, cursor) && "bg-muted/30")}>
              <div className={cn("text-xs w-6 h-6 grid place-items-center rounded-full", isSameDay(day, new Date()) ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground")}>{format(day, "d")}</div>
              {items.slice(0, 3).map((it) => <Chip key={`${it.kind}-${it.id}`} item={it} navigate={navigate} isAdmin={isAdmin} />)}
              {items.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{items.length - 3}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ range, itemsForDay, navigate, isAdmin }) {
  const days = eachDayOfInterval({ start: range.start, end: range.end });
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
      {days.map((day) => {
        const items = itemsForDay(day);
        return (
          <div key={day.toISOString()} className="rounded-lg border bg-card p-2 min-h-[160px]">
            <div className={cn("text-xs font-medium mb-2 flex items-center justify-between", isSameDay(day, new Date()) && "text-primary")}>
              <span className="capitalize">{format(day, "EEE d", { locale: it })}</span>
              {items.length > 0 && <span className="text-muted-foreground">{items.length}</span>}
            </div>
            <div className="space-y-1">{items.map((it) => <Chip key={`${it.kind}-${it.id}`} item={it} navigate={navigate} isAdmin={isAdmin} />)}</div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ day, items, navigate, isAdmin, onEdit, onDelete }) {
  return (
    <div className="rounded-lg border bg-card divide-y">
      {items.length === 0 ? <div className="p-12 text-center text-muted-foreground text-sm">Nessun appuntamento in questa giornata.</div> :
        items.map((it) => (
          it.kind === "google" ? (
            <button key={`g-${it.id}`} data-testid={`gcal-event-${it.id}`} onClick={() => it.html_link && window.open(it.html_link, "_blank")} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 text-left transition-colors">
              <div className="font-mono text-sm w-14">{it.all_day ? "—" : (() => { try { return format(parseISO(it.start_at), "HH:mm"); } catch { return ""; } })()}</div>
              <span className="w-2 h-8 rounded-full" style={{ background: (isAdmin && it.owner_color) || "#38bdf8" }} />
              <div className="flex-1">
                <div className="font-medium flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-sky-500" />{it.title}</div>
                <div className="text-xs text-muted-foreground">Evento Google Calendar{isAdmin && it.owner_name ? ` · ${it.owner_name}` : ""}</div>
              </div>
              <span className="text-xs px-2 py-0.5 rounded border border-sky-400 text-sky-600 dark:text-sky-300">Google</span>
            </button>
          ) : (
            <div key={`c-${it.id}`} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
              <button data-testid={`appt-${it.id}`} onClick={() => it.lead && navigate(`/leads/${it.lead.id}`)} className="flex items-center gap-3 flex-1 text-left min-w-0">
                <div className="font-mono text-sm w-24" data-testid={`appt-time-${it.id}`}>{format(parseISO(it.start_at), "HH:mm")}–{format(addMinutes(parseISO(it.start_at), it.duration || 30), "HH:mm")}</div>
                <span className="w-2 h-8 rounded-full" style={{ background: (isAdmin && it.user?.color) || undefined }} />
                <span className={cn("w-2 h-8 rounded-full", !isAdmin || !it.user?.color ? MODE_COLOR[it.mode] : "hidden")} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.lead ? fullName(it.lead) : "—"}</div>
                  <div className="text-xs text-muted-foreground">{it.tipologia ? `${it.tipologia} · ` : ""}{it.mode} · {it.duration} min{it.user?.name ? ` · ${it.user.name}` : ""}</div>
                </div>
              </button>
              <span className="text-xs px-2 py-0.5 rounded bg-secondary">{it.status}</span>
              {(it.status === "DA FARE" || it.status === "RIPROGRAMMATO") && (
                <button data-testid={`appt-edit-${it.id}`} onClick={() => onEdit && onEdit(it)} title="Modifica appuntamento"
                  className="w-8 h-8 grid place-items-center rounded-md border hover:bg-accent shrink-0"><Pencil className="w-4 h-4" /></button>
              )}
              {(it.status === "DA FARE" || it.status === "RIPROGRAMMATO") && (
                <button data-testid={`appt-delete-${it.id}`} onClick={() => onDelete && onDelete(it)} title="Elimina appuntamento"
                  className="w-8 h-8 grid place-items-center rounded-md border border-red-500/30 text-red-600 hover:bg-red-500/10 shrink-0"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          )
        ))}
    </div>
  );
}
